import {
    ChangeStatus,
    EBookingStatus,
    ECalendarStatus,
    ESystemStatus,
    IBookableResourceBooking,
    IEvent,
    IOtherSiteEvent
} from "../../interfaces/event";
import { IIncidentType } from "../../interfaces/incidentTypes";
import { AvailabilityType, DayOfWeek, IBookableresourceAvailability, IContractedHours, IOtherSiteWorkOrder, IResource, ITimeOffRequest, IWorkOrder, IWorkOrderType, RepeatPattern, ShiftCancellationRequestStatus } from "../../interfaces/resource";
import addMinutes from "date-fns/addMinutes";
import { addDays, differenceInDays, differenceInMinutes, endOfDay, format, isSameDay, startOfDay,  } from "date-fns";
import { Frequency, RRule, Weekday } from "rrule";

const shiftsFormatter = (apiShift: IWorkOrder, shiftTypes: IWorkOrderType[], incidentTypes: IIncidentType[]): IEvent | undefined => {
    if (apiShift.msdyn_systemstatus === ESystemStatus.Canceled) {
        return;
    }

    const bidders = apiShift.msdyn_msdyn_workorder_bookableresourcebooking_WorkOrder.filter(x => x["_bookingstatus_value@OData.Community.Display.V1.FormattedValue"]?.toLocaleLowerCase() == 'expressed interest'); //Expressed interest is the display name - id can't be used as it might change in prod
    const assignedWorkBookings = apiShift.msdyn_msdyn_workorder_bookableresourcebooking_WorkOrder.filter(x => x["_bookingstatus_value@OData.Community.Display.V1.FormattedValue"]?.toLocaleLowerCase() != 'expressed interest'
        && x["_bookingstatus_value@OData.Community.Display.V1.FormattedValue"]?.toLocaleLowerCase() != 'canceled' && x["_bookingstatus_value@OData.Community.Display.V1.FormattedValue"]?.toLocaleLowerCase() != 'coordinator canceled');
    const event: Partial<IEvent> = {}

    if (assignedWorkBookings.length > 0) {
        const assignedBooking = assignedWorkBookings[0];
        event.resourceId = assignedBooking['_resource_value']
        event.originalResourceID = assignedBooking['_resource_value']
        event.bookableResourceStatus = assignedBooking['_bookingstatus_value'] as any;
        event.BookingId = assignedBooking.bookableresourcebookingid;
        event.isCancellationRequestPendingOriginal = assignedBooking.illumina_cancellationrequeststatus == ShiftCancellationRequestStatus.Pending;
        event.cancellationRequestComments = assignedBooking.illumina_cancellationrequestcomments;

    } else {
        event.resourceId = '0'
        event.originalResourceID = '0'
        event.bookableResourceStatus = EBookingStatus.Scheduled
        event.BookingId = '';
    }


    const primaryIncidentType = () => {
        if (apiShift.msdyn_primaryincidenttype && apiShift.msdyn_primaryincidenttype.msdyn_incidenttypeid) {
            return incidentTypes.find(type => {
                return type.id === apiShift.msdyn_primaryincidenttype.msdyn_incidenttypeid
            })
        }
        return {
            name: 'Not Required',
            id: '0',
            characteristic: []
        }
    }
    const sd = new Date(apiShift.msdyn_timewindowstart);
    const ed = apiShift.msdyn_totalestimatedduration ? addMinutes(sd, apiShift.msdyn_totalestimatedduration) : new Date(apiShift.msdyn_timewindowend); // if duration exists, use duration. Otherwise use enddate
    const shiftDuration = apiShift.msdyn_totalestimatedduration ? apiShift.msdyn_totalestimatedduration : differenceInMinutes(ed, sd)
    return {
        title: apiShift.msdyn_name,
        start: sd,
        end: ed,
        id: apiShift.msdyn_workorderid,
        description: apiShift.msdyn_workordersummary ? apiShift.msdyn_workordersummary : apiShift.msdyn_name,
        status: apiShift.msdyn_systemstatus,
        preferredResourceId: apiShift._msdyn_preferredresource_value,
        preferredResourceName: apiShift['_msdyn_preferredresource_value@OData.Community.Display.V1.FormattedValue'],
        jobType: apiShift['illumina_jobtype@OData.Community.Display.V1.FormattedValue'],
        displayColor: shiftTypes.find(x => x.msdyn_workordertypeid == apiShift._msdyn_workordertype_value)?.illumina_displaycolour,
        incidentType: primaryIncidentType(),
        bidders: bidders.map(x => <IBookableResourceBooking>{
            bookingStatus: x._bookingstatus_value,
            id: x.bookableresourcebookingid,
            bookableResourceId: x._resource_value
        }),
        calendarStatus: (event.resourceId === '0') ? ECalendarStatus.scheduled : ECalendarStatus.assigned,
        serviceAccount: {
            name: apiShift.msdyn_serviceaccount.name,
            accountId: apiShift.msdyn_serviceaccount.accountid,
            accountNumber: apiShift.msdyn_serviceaccount.accountnumber
        },
        serviceContact: {
            fullName: apiShift.illumina_ServiceContact?.fullname,
            contactId: apiShift.illumina_ServiceContact?.contactid,
         },
        workOrderType: {
            msdyn_workordertypeid: apiShift.msdyn_workordertype?.msdyn_workordertypeid,
            msdyn_name: apiShift.msdyn_workordertype?.msdyn_name,
            illumina_displaycolour: apiShift.msdyn_workordertype?.illumina_displaycolour
        },
        isJobBidding: apiShift.illumina_openforshiftjobbidding,
        changeStatus: ChangeStatus.UnChanged,
        shiftLength: shiftDuration,
        loading: false,
        ...event
    } as IEvent
}

const leaveFormatter = (leave: ITimeOffRequest, resource: IResource): IEvent => {
    return {
        title: "On Leave",
        start: leave.start,
        end: leave.end,
        id: leave.id,
        description: '',
        resourceId: resource.id,
        originalResourceID: resource.id,
        changeStatus: ChangeStatus.UnChanged,
        loading: false,
        editable: false,
        display: 'unavailable',
        resourceEditable: false
    } as IEvent
}

const otherSiteShiftFormatter = (apiShift: IOtherSiteWorkOrder, shiftTypes: IWorkOrderType[]): IOtherSiteEvent | undefined => {
    const assignedWorkBookings = apiShift.msdyn_msdyn_workorder_bookableresourcebooking_WorkOrder.filter(x => x["_bookingstatus_value@OData.Community.Display.V1.FormattedValue"]?.toLocaleLowerCase() != 'expressed interest'
        && x["_bookingstatus_value@OData.Community.Display.V1.FormattedValue"] != 'Canceled' && x["_bookingstatus_value@OData.Community.Display.V1.FormattedValue"]?.toLocaleLowerCase() != 'coordinator canceled');

    const sd = new Date(apiShift.msdyn_timewindowstart);
    const ed = apiShift.msdyn_totalestimatedduration
        ? addMinutes(sd, apiShift.msdyn_totalestimatedduration)
        : new Date(apiShift.msdyn_timewindowend);
    const shiftDuration = apiShift.msdyn_totalestimatedduration ? apiShift.msdyn_totalestimatedduration : differenceInMinutes(ed, sd)

    if (assignedWorkBookings.length > 0) {
        const event: IOtherSiteEvent = {
            id: apiShift.msdyn_workorderid,
            editable: false,
            resourceEditable: false,
            startEditable: false,
            otherSite: true,
            resourceId: assignedWorkBookings[0]._resource_value,
            title: apiShift.msdyn_name,
            start: sd,
            end: ed,
            shiftLength: shiftDuration,
            displayColor: shiftTypes.find(x => x.msdyn_workordertypeid == apiShift._msdyn_workordertype_value)?.illumina_displaycolour,
            jobType: apiShift.illumina_jobtype,
            customerFullName: apiShift.illumina_ServiceContact?.fullname || "Customer Missing",
            siteLocation: apiShift["_illumina_sitelocationfromid_value@OData.Community.Display.V1.FormattedValue"] || "Site Location missing",
            workOrderType: {
                msdyn_workordertypeid: apiShift.msdyn_workordertype?.msdyn_workordertypeid,
                msdyn_name: apiShift.msdyn_workordertype?.msdyn_name,
                illumina_displaycolour: apiShift.msdyn_workordertype?.illumina_displaycolour
            }

        }

        return event;
    }


}

const availabilityFormatter = (resourceId: string, availability: IBookableresourceAvailability): IEvent => {
    let start = new Date(availability.illumina_startdate);
    let end = new Date(availability.illumina_enddate);
    let shiftLength = availability.illumina_allday ? 24 * 60 : differenceInMinutes(new Date(availability.illumina_enddate), new Date(availability.illumina_startdate));

    let event = {
        resourceId: resourceId,
        title: `${availability["illumina_availabilitytype@OData.Community.Display.V1.FormattedValue"]}`,
        id: availability.illumina_availabilityid,
        display: availability.illumina_availabilitytype === AvailabilityType.ContractedHours ? 'contractedHours' : 'auto',
        shiftLength: shiftLength,
        description: '',
        editable: false,
        start: start,
        end: end,
        availabilityType: availability.illumina_availabilitytype,
        resourceEditable: false
    } as IEvent;

    if (isSameDay(start, end)) {
        event.start = start;
        event.allDay = availability.illumina_allday;
        event.end = end
        return event;
    }

    if (!availability.illumina_repeatpattern) {
        event.start = start;
        event.allDay = availability.illumina_allday;
        event.end = availability.illumina_allday ? endOfDay(end) : end;
        event.end = availability.illumina_allday ? addDays(event.end, 1) : event.end;
        return event;
    }


    if (availability.illumina_allday) {
        start = startOfDay(start);
        end = endOfDay(end);
        event.allDay = true;
    }
    else {
        let startDateTime = start;
        let endDateTime = new Date(start.getFullYear(), start.getMonth(), start.getDate(), end.getHours(), end.getMinutes());
        if (endDateTime < startDateTime) //if time span over multilpe days, for example 1st Jan 24 11PM to 2nd Jan 24 2AM
            endDateTime = addDays(endDateTime, 1);

        let diffInTotalMins = differenceInMinutes(endDateTime, startDateTime);
        let diffInHours = Math.trunc(diffInTotalMins / 60);
        let diffInMins = diffInTotalMins - (diffInHours * 60);
        event.duration = `${diffInHours.toString().padStart(2, '0')}:${diffInMins.toString().padStart(2, '0')}`;
    }
    let until = end;

    switch (availability.illumina_repeatpattern) {
        case RepeatPattern.Daily:
            event.rrule = {
                freq: Frequency.DAILY,
                interval: availability.illumina_repeatoccurence,
                dtstart: start.toISOString(),
                until: until.toISOString()
            };
            break;
        case RepeatPattern.Weekly:
            let days = availability.illumina_days.split(',').map(d => {
                switch (parseInt(d)) {
                    case DayOfWeek.Friday:
                        return RRule.FR;
                    case DayOfWeek.Monday:
                        return RRule.MO;
                    case DayOfWeek.Saturday:
                        return RRule.SA;
                    case DayOfWeek.Sunday:
                        return RRule.SU;
                    case DayOfWeek.Thursday:
                        return RRule.TH;
                    case DayOfWeek.Tuesday:
                        return RRule.TU;
                    case DayOfWeek.Wednesday:
                        return RRule.WE;
                }
            });

            event.rrule = {
                freq: Frequency.WEEKLY,
                interval: availability.illumina_repeatoccurence,
                byweekday: days, //['mo', 'fr']
                dtstart: availability.illumina_allday ? start : format(start, "yyyy-MM-dd'T'HH:mm:ss"),
                until: availability.illumina_allday ? until : format(until, "yyyy-MM-dd'T'HH:mm:ss")
            };
            break;   
    }
    return event as IEvent;
}


export const availabilityFortnightlyFormatter = (resourceId: string, availability: IBookableresourceAvailability): IEvent[] => {
    const week1Events: IEvent[] = [];
    const week2Events: IEvent[] = [];
    let endDate = new Date(availability.illumina_enddate);
    let startDate = new Date(availability.illumina_startdate);
    let shiftLength = availability.illumina_allday ? 24 * 60 : differenceInMinutes(new Date(availability.illumina_enddate), new Date(availability.illumina_startdate));
    
    // Calculate the difference in days between startDate and endDate
    let dateDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    if (availability.illumina_allday) {
        dateDiff = dateDiff + 1; // If it's an all-day event, include the end date
    }

    let event = {
        display: 'auto',
        description: '',
        editable: false,
        resourceId: resourceId,
        resourceEditable: false,
        shiftLength: shiftLength,
        id: availability.illumina_availabilityid,
        availabilityType: availability.illumina_availabilitytype,
        title: `${availability["illumina_availabilitytype@OData.Community.Display.V1.FormattedValue"]}`
    } as IEvent;

    let fortnightlyWeek1 = availability.illumina_week1 ? availability.illumina_week1.split(',').map(d => {
        switch (parseInt(d)) {
            case DayOfWeek.Friday:
                return RRule.FR;
            case DayOfWeek.Monday:
                return RRule.MO;
            case DayOfWeek.Saturday:
                return RRule.SA;
            case DayOfWeek.Sunday:
                return RRule.SU;
            case DayOfWeek.Thursday:
                return RRule.TH;
            case DayOfWeek.Tuesday:
                return RRule.TU;
            case DayOfWeek.Wednesday:
                return RRule.WE;
        }
    }): [];

    let fortnightlyWeek2 = availability.illumina_week2 ? availability.illumina_week2.split(',').map(d => {
        switch (parseInt(d)) {
            case DayOfWeek.Friday:
                return RRule.FR;
            case DayOfWeek.Monday:
                return RRule.MO;
            case DayOfWeek.Saturday:
                return RRule.SA;
            case DayOfWeek.Sunday:
                return RRule.SU;
            case DayOfWeek.Thursday:
                return RRule.TH;
            case DayOfWeek.Tuesday:
                return RRule.TU;
            case DayOfWeek.Wednesday:
                return RRule.WE;
        }
    }): [];


    const convertDayToRRuleDay = (day: number): Weekday => {
        switch (day) {
            case 0: return RRule.SU;
            case 1: return RRule.MO;
            case 2: return RRule.TU;
            case 3: return RRule.WE;
            case 4: return RRule.TH;
            case 5: return RRule.FR;
            case 6: return RRule.SA;
            default: throw new Error(`Invalid day: ${day}`);
        }
    };

    let weekNo = 1;
    for (let i = 0; i < dateDiff; i++) {
        var currentDate = addDays(startDate, i);

        if (i > 0 && currentDate.getDay() === 1)
            weekNo++;

        const rruleDay = convertDayToRRuleDay(currentDate.getDay());
        var start = currentDate;
        var end = currentDate;
        event.id = availability.illumina_availabilityid + currentDate.getTime().toString();
        if (availability.illumina_allday) {
            start = startOfDay(currentDate);
            end = endOfDay(currentDate);
            event.allDay = true;
        }
        else {
            
            end = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), endDate.getHours(), endDate.getMinutes());
            if (end < start) //if time span over multilpe days, for example 1st Jan 24 11PM to 2nd Jan 24 2AM
                end = addDays(end, 1);
    
            let diffInTotalMins = differenceInMinutes(end, start);
            let diffInHours = Math.trunc(diffInTotalMins / 60);
            let diffInMins = diffInTotalMins - (diffInHours * 60);
            event.duration = `${diffInHours.toString().padStart(2, '0')}:${diffInMins.toString().padStart(2, '0')}`;
        }

        if (weekNo % 2 !== 0) { 
            // Week 1
            if (fortnightlyWeek1.includes(rruleDay)) {
                const newEvent: IEvent = {
                    ...event,
                    end: end,
                    start: start
                };
                week1Events.push(newEvent);
            }

        } else if (weekNo % 2 === 0) {
            // Week 2
            if (fortnightlyWeek2.includes(rruleDay)) {
                const newEvent: IEvent = {
                    ...event,
                    end: end,
                    start: start
                };
                week2Events.push(newEvent);
            }
        }
        
    }

    return [...week1Events, ...week2Events];
}

const contractedHoursFormatter = (availability: IBookableresourceAvailability): IContractedHours[] => {
    const repeatPattern = availability.illumina_repeatpattern;
    if (repeatPattern === RepeatPattern.Daily) {
        return contractedHoursDailyFormatter(availability);
    } else if (repeatPattern === RepeatPattern.Weekly) {
        return contractedHoursWeeklyFormatter(availability);
    } else if (repeatPattern === RepeatPattern.Fortnightly) {
        return contractedHoursFortnightlyFormatter(availability);
    } else {
        const resultSplitDate = arrangeDate(new Date(availability.illumina_startdate), new Date(availability.illumina_enddate))
        const arrContractedHours: IContractedHours[] = resultSplitDate.map(item => {
            return {
                type: availability.illumina_contractedhourstype,
                startDate: item.startTime,
                endDate: item.endTime,
                id: availability.illumina_availabilityid
            }
        })
        return arrContractedHours;
    }
}

const contractedHoursDailyFormatter = (availability: IBookableresourceAvailability): IContractedHours[] => {
    const startDate = new Date(availability.illumina_startdate);
    const endDate = new Date(availability.illumina_enddate);
    const dateDiff = differenceInDays(endDate, startDate) + 1;
    let resultContractedHours: IContractedHours[] = [];
    for (let i = 0; i < dateDiff; i++) {
        const currentDate = addDays(startDate, i);
        let start = currentDate;
        let end = currentDate;
        if (availability.illumina_allday) {
            start = startOfDay(currentDate);
            end = endOfDay(currentDate);
        } else {
            end = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                currentDate.getDate(),
                endDate.getHours(),
                endDate.getMinutes()
            );

            if (end < start) {
                end = addDays(end, 1);
            }
        }
        const resultSplitDate = arrangeDate(start, end);
        const arrContractedHours: IContractedHours[] = resultSplitDate.map(item => {
            return {
                type: availability.illumina_contractedhourstype,
                startDate: item.startTime,
                endDate: item.endTime,
                id: availability.illumina_availabilityid
            }
        })
        resultContractedHours = resultContractedHours.concat(arrContractedHours);
    }

    return resultContractedHours;
}

const contractedHoursWeeklyFormatter = (availability: IBookableresourceAvailability): IContractedHours[] => {
    const startDate = new Date(availability.illumina_startdate);
    const endDate = new Date(availability.illumina_enddate);
    const dateDiff = differenceInDays(endDate, startDate) + 1;
    let weekNo = 1;
    const repeatDays = availability.illumina_days
    ? availability.illumina_days
        .split(",")
        .map((d: string) => daysOfWeekMap.get(parseInt(d)))
        .filter((d: number | undefined) => d !== undefined)
    : [];
    let resultContractedHours: IContractedHours[] = [];

    for (let i = 0; i < dateDiff; i++) {
        const currentDate = addDays(startDate, i);
        // start of week is tuesday
        if (i > 0 && currentDate.getDay() === 2) {
            weekNo++;
        }

        if ((weekNo - 1) % (availability.illumina_repeatoccurence ?? 1) === 0) {
            const dayOfWeek = currentDate.getDay();
            if (repeatDays.includes(dayOfWeek)) {
                let start = currentDate;
                let end = currentDate;
                if (availability.illumina_allday) {
                    start = startOfDay(currentDate);
                    end = endOfDay(currentDate);
                } else {
                    end = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), endDate.getHours(), endDate.getMinutes());
                    if (end < start) {
                        end = addDays(end, 1);
                    }
                }
                const resultSplitDate = arrangeDate(start, end);
                const arrContractedHours: IContractedHours[] = resultSplitDate.map(item => {
                    return {
                        type: availability.illumina_contractedhourstype,
                        startDate: item.startTime,
                        endDate: item.endTime,
                        id: availability.illumina_availabilityid
                    }
                })
                resultContractedHours = resultContractedHours.concat(arrContractedHours);
            }
        }
    }
    return resultContractedHours;
}

const contractedHoursFortnightlyFormatter = (availability: IBookableresourceAvailability): IContractedHours[] => {
    const startDate = new Date(availability.illumina_startdate);
    const endDate = new Date(availability.illumina_enddate);
    const dateDiff = differenceInDays(endDate, startDate) + 1;
    let weekNo = 1;

    const fortnightlyWeek1 = availability.illumina_week1
      ? availability.illumina_week1
          .split(",")
          .map((d: string) => daysOfWeekMap.get(parseInt(d)))
          .filter((d: number | undefined) => d !== undefined)
      : [];

    const fortnightlyWeek2 = availability.illumina_week2
      ? availability.illumina_week2
          .split(",")
          .map((d: string) => daysOfWeekMap.get(parseInt(d)))
          .filter((d: number | undefined) => d !== undefined)
      : [];
    
    let resultContractedHours: IContractedHours[] = [];

    for (let i = 0; i < dateDiff; i++) {
        const currentDate = addDays(startDate, i);
        // day of week start on tuesday
        if (i > 0 && currentDate.getDay() === 2) {
          weekNo++;
        }

        let start = currentDate;
        let end = currentDate;
        if (availability.illumina_allday) {
            start = startOfDay(currentDate);
            end = endOfDay(currentDate);
        } else {
            end = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), endDate.getHours(), endDate.getMinutes());
            if (end < start) {
                end = addDays(end, 1);
            }
        }

        if (weekNo % 2 !== 0) {
            if (fortnightlyWeek1.includes(currentDate.getDay())) {
                const resultSplitDate = arrangeDate(start, end);
                const arrContractedHours: IContractedHours[] = resultSplitDate.map(item => {
                    return {
                        type: availability.illumina_contractedhourstype,
                        startDate: item.startTime,
                        endDate: item.endTime,
                        id: availability.illumina_availabilityid
                    }
                })
                resultContractedHours = resultContractedHours.concat(arrContractedHours);
            }
        } else {
            if (fortnightlyWeek2.includes(currentDate.getDay())) {
                const resultSplitDate = arrangeDate(start, end);
                const arrContractedHours: IContractedHours[] = resultSplitDate.map(item => {
                    return {
                        type: availability.illumina_contractedhourstype,
                        startDate: item.startTime,
                        endDate: item.endTime,
                        id: availability.illumina_availabilityid
                    }
                })
                resultContractedHours = resultContractedHours.concat(arrContractedHours);
            }
          }
    }

    return resultContractedHours;
}

const daysOfWeekMap = new Map<number, number>([
    [DayOfWeek.Sunday, 0],
    [DayOfWeek.Monday, 1],
    [DayOfWeek.Tuesday, 2],
    [DayOfWeek.Wednesday, 3],
    [DayOfWeek.Thursday, 4],
    [DayOfWeek.Friday, 5],
    [DayOfWeek.Saturday, 6],
  ]);

function arrangeDate(startDate: Date, endDate: Date) {
    const start = new Date(startDate);
    const end = new Date(endDate);
  
    const events = [];
  
    if (isSameDay(start, end)) {
      return [{
        startTime: start,
        endTime: end,
      }];
    }

    events.push({
      startTime: start,
      endTime: endOfDay(start),
    });
  
    
    let currentDay = addDays(start, 1); 
    while (!isSameDay(currentDay, end) && currentDay < end) {
      events.push({
        startTime: startOfDay(currentDay),
        endTime: endOfDay(currentDay),
      });
      currentDay = addDays(currentDay, 1);
    }
  
    events.push({
      startTime: startOfDay(end),
      endTime: end, 
    });
  
    return events;
}

export { shiftsFormatter, otherSiteShiftFormatter, leaveFormatter, availabilityFormatter, contractedHoursFormatter }